// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/api-client.js';
import { useLocale } from '../hooks/useLocale.js';
import { usePageTitle } from '../hooks/usePageTitle.js';

export function ResetPasswordPage() {
  usePageTitle('Reset Password');
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const rules = [
    { test: (p: string) => p.length >= 12, label: t.auth.reset_password.rules.min_length },
    { test: (p: string) => /[A-Z]/.test(p), label: t.auth.reset_password.rules.uppercase },
    { test: (p: string) => /[a-z]/.test(p), label: t.auth.reset_password.rules.lowercase },
    { test: (p: string) => /[0-9]/.test(p), label: t.auth.reset_password.rules.digit },
    { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: t.auth.reset_password.rules.special },
  ];

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setValidationErrors([]);

    if (!token) {
      setError(t.auth.reset_password.invalid_token);
      return;
    }

    const errors = rules.filter((rule) => !rule.test(newPassword)).map((rule) => rule.label);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t.auth.reset_password.password_mismatch);
      return;
    }

    setLoading(true);

    try {
      await apiClient.resetPassword({ token, new_password: newPassword });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t.common.operation_failed);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>{t.auth.reset_password.title}</h1>

        {success ? (
          <div>
            <p className="dm-alert dm-alert--success" role="status">
              {t.auth.reset_password.success}
            </p>
            <Link to="/login">{t.auth.reset_password.go_to_login}</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p>{t.auth.reset_password.subtitle}</p>

            {error && (
              <p className="dm-form-error" role="alert">
                {error}
              </p>
            )}

            {validationErrors.length > 0 && (
              <div className="dm-alert dm-alert--danger" role="alert">
                <p>{t.auth.reset_password.password_requirements}</p>
                <ul>
                  {validationErrors.map((ve, i) => (
                    <li key={i}>{ve}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="new-password">{t.auth.reset_password.new_password}</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">{t.auth.reset_password.confirm_password}</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="password-requirements">
              <p>
                <strong>{t.auth.reset_password.requirements_title}</strong>
              </p>
              <ul>
                {rules.map((rule, i) => (
                  <li key={i} className={rule.test(newPassword) ? 'met' : 'unmet'}>
                    {rule.label}
                  </li>
                ))}
              </ul>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? t.auth.reset_password.resetting : t.auth.reset_password.reset}
            </button>

            <p>
              <Link to="/login">{t.auth.forgot_password.back_to_login}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
